import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { api, formatError } from "../lib/api";
import { logger } from "../lib/logger";
import {
  Building2, Copy as CopyIcon, Download, Image as ImageIcon, Loader2,
  Lock, Send, Settings2, Share2, Sparkles, Wand2,
} from "lucide-react";

const PLATFORM_ICONS = {
  facebook: "🅵", instagram: "📷", twitter: "𝕏", tiktok: "🎵",
  linkedin: "in", youtube_short: "▶︎", reddit: "🅡", pinterest: "📍",
};

const DEFAULT_BRAND = {
  business_name: "", tagline: "", value_props: "", target_audience: "",
  brand_voice: "", primary_color: "#1d4ed8", logo_url: "", website: "",
};

export default function BusinessAdsPage() {
  const [accessLoading, setAccessLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [selected, setSelected] = useState([]);
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [brandOpen, setBrandOpen] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [offer, setOffer] = useState("");
  const [includeImages, setIncludeImages] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [ads, setAds] = useState([]);
  const [campaignId, setCampaignId] = useState(null);
  const [creditsRemaining, setCreditsRemaining] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [accRes, platRes, kitRes] = await Promise.all([
          api.get("/business/access"),
          api.get("/business/platforms"),
          api.get("/business/brand-kit").catch(() => ({ data: {} })),
        ]);
        setAccess(accRes.data);
        setPlatforms(platRes.data);
        setSelected(platRes.data.map((p) => p.id));
        if (kitRes.data && Object.keys(kitRes.data).length) {
          setBrand({ ...DEFAULT_BRAND, ...kitRes.data });
        }
      } catch (e) {
        logger.error("BusinessAds bootstrap failed", e);
      } finally {
        setAccessLoading(false);
      }
    })();
  }, []);

  const allSelected = useMemo(
    () => platforms.length > 0 && selected.length === platforms.length,
    [platforms, selected],
  );

  const togglePlatform = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const toggleAll = () =>
    setSelected(allSelected ? [] : platforms.map((p) => p.id));

  const onBrandChange = (field, value) =>
    setBrand((b) => ({ ...b, [field]: value }));

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      await api.post("/business/brand-kit", brand);
      toast.success("Brand kit saved.");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    } finally {
      setSavingBrand(false);
    }
  };

  const totalCredits = useMemo(() => {
    const text = selected.length * 2;
    const img = includeImages ? selected.length * 5 : 0;
    return text + img;
  }, [selected, includeImages]);

  const generate = async () => {
    if (offer.trim().length < 10) {
      toast.error("Describe your offer in a sentence or two.");
      return;
    }
    if (selected.length === 0) {
      toast.error("Pick at least one platform.");
      return;
    }
    setGenerating(true);
    setAds([]);
    try {
      const { data } = await api.post("/business/ads/generate", {
        offer: offer.trim(),
        platforms: selected,
        include_images: includeImages,
        provider: "anthropic",
      });
      setAds(data.ads || []);
      setCampaignId(data.campaign_id);
      setCreditsRemaining(data.credits_remaining);
      const succeeded = data.ads_succeeded ?? data.ads.filter((a) => a.copy && !a.error).length;
      const failed = data.ads_failed ?? data.ads.filter((a) => !a.copy || a.error).length;
      if (failed > 0 && succeeded > 0) {
        toast.warning(`${succeeded} ad${succeeded === 1 ? "" : "s"} generated, ${failed} failed. Check each card for details.`);
      } else {
        toast.success(`Generated ${succeeded} ad${succeeded === 1 ? "" : "s"}. ${data.credits_used} credits used.`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : formatError(detail);
      toast.error(msg || "Generation failed. Please check your API keys and try again.", { duration: 8000 });
    } finally {
      setGenerating(false);
    }
  };

  const copyAd = async (ad) => {
    const text = (ad.copy || "") + (ad.hashtags?.length ? "\n\n" + ad.hashtags.join(" ") : "");
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${ad.platform_name} ad.`);
  };

  const downloadOne = (ad) => {
    const text = (ad.copy || "") + (ad.hashtags?.length ? "\n\n" + ad.hashtags.join(" ") : "");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ad.platform}-ad.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadImage = (ad) => {
    if (!ad.image_b64) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${ad.image_b64}`;
    a.download = `${ad.platform}-ad-image.png`;
    a.click();
  };

  const downloadAll = () => {
    if (!ads.length) return;
    const lines = ads.map(
      (ad) =>
        `=== ${ad.platform_name} (${ad.char_count}/${ad.char_limit} chars) ===\n\n${ad.copy}\n\n${(ad.hashtags || []).join(" ")}\n`,
    );
    const blob = new Blob([lines.join("\n\n---\n\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaign-${campaignId || "all"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (accessLoading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </AppShell>
    );
  }

  if (!access?.has_access) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto py-16 px-4">
          <div className="border-2 border-zinc-900 dark:border-zinc-100 bg-white dark:bg-zinc-950 p-8 md:p-12 rounded-none">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-7 h-7" />
              <h1 className="text-2xl md:text-3xl font-black">Business Ad Generator</h1>
            </div>
            <p className="text-base text-zinc-700 dark:text-zinc-300 mb-6">
              Generate platform-tailored ads for Facebook, Instagram, X, TikTok, LinkedIn,
              YouTube Shorts, Reddit, and Pinterest — in one click. Each ad respects character
              limits, hashtag conventions, and platform voice. Add a brand kit and your ads
              auto-write in your business's tone.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <div className="border border-zinc-300 dark:border-zinc-700 p-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Business Monthly</div>
                <div className="text-3xl font-black mb-2">$149<span className="text-base font-medium text-zinc-500">/mo</span></div>
                <ul className="text-sm text-zinc-700 dark:text-zinc-300 space-y-1">
                  <li>2,000 credits/mo</li>
                  <li>All 8 platforms</li>
                  <li>Brand kit + image generation</li>
                </ul>
              </div>
              <div className="border-2 border-zinc-900 dark:border-zinc-100 p-4 relative">
                <div className="absolute -top-3 left-4 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-[10px] font-bold uppercase px-2 py-0.5">Limited 50 seats</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Business Lifetime</div>
                <div className="text-3xl font-black mb-2">$497<span className="text-base font-medium text-zinc-500"> once</span></div>
                <ul className="text-sm text-zinc-700 dark:text-zinc-300 space-y-1">
                  <li>100,000 credits forever</li>
                  <li>Never billed again</li>
                  <li>Lifetime access + future features</li>
                </ul>
              </div>
            </div>
            <Link
              to="/pricing"
              data-testid="business-upgrade-cta"
              className="inline-flex items-center gap-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-6 py-3 font-bold hover:bg-zinc-700 dark:hover:bg-zinc-300 transition"
            >
              <Sparkles className="w-4 h-4" /> Upgrade to Business
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="business-ads-page">
        <header className="border-b-2 border-zinc-900 dark:border-zinc-100 pb-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6" />
            <h1 className="text-2xl md:text-3xl font-black">Business Ad Generator</h1>
          </div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2">
            One offer → 8 platform-perfect ads. Powered by Claude Sonnet 4.5.
          </p>
        </header>

        {/* Brand Kit */}
        <section className="border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950">
          <button
            type="button"
            onClick={() => setBrandOpen((o) => !o)}
            className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition"
            data-testid="brand-kit-toggle"
          >
            <span className="flex items-center gap-2 font-bold">
              <Settings2 className="w-4 h-4" /> Brand Kit
              {brand.business_name && (
                <span className="text-xs font-normal text-zinc-500">— {brand.business_name}</span>
              )}
            </span>
            <span className="text-xs text-zinc-500">{brandOpen ? "Hide" : "Show"}</span>
          </button>
          {brandOpen && (
            <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 grid sm:grid-cols-2 gap-3">
              {[
                ["business_name", "Business name", "AcmeTeas"],
                ["website", "Website", "acmeteas.com"],
                ["tagline", "Tagline", "Single-origin teas, delivered fresh"],
                ["target_audience", "Target audience", "Tea lovers ages 25-45"],
                ["value_props", "Value propositions", "Direct from farm; rare cultivars"],
                ["brand_voice", "Brand voice", "Warm, knowledgeable, slightly poetic"],
              ].map(([f, label, placeholder]) => (
                <label key={f} className="block">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">{label}</span>
                  <input
                    type="text"
                    value={brand[f] || ""}
                    onChange={(e) => onBrandChange(f, e.target.value)}
                    placeholder={placeholder}
                    data-testid={`brand-${f}`}
                    className="mt-1 w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
                  />
                </label>
              ))}
              <div className="sm:col-span-2 flex justify-end">
                <button
                  type="button"
                  onClick={saveBrand}
                  disabled={savingBrand}
                  data-testid="brand-save-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-bold disabled:opacity-50"
                >
                  {savingBrand ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Save Brand Kit
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Offer + platforms */}
        <section className="border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-4 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500">What are you advertising?</span>
            <textarea
              value={offer}
              onChange={(e) => setOffer(e.target.value)}
              rows={4}
              placeholder="E.g. Launch our Spring 2026 First-Flush Darjeeling collection. 15% off first order with code BLOOM15."
              data-testid="ad-offer-input"
              className="mt-1 w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
            />
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest text-zinc-500">Platforms</span>
              <button type="button" onClick={toggleAll} className="text-xs underline" data-testid="toggle-all-platforms">
                {allSelected ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {platforms.map((p) => {
                const on = selected.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePlatform(p.id)}
                    data-testid={`platform-${p.id}`}
                    className={`flex items-center gap-2 px-3 py-2 border text-sm transition ${
                      on
                        ? "border-zinc-900 dark:border-zinc-100 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900"
                        : "border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-zinc-500"
                    }`}
                  >
                    <span className="w-5 text-center font-bold">{PLATFORM_ICONS[p.id] || "•"}</span>
                    <span className="flex-1 text-left">{p.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={includeImages}
                onChange={(e) => setIncludeImages(e.target.checked)}
                data-testid="include-images-toggle"
                className="w-4 h-4"
              />
              <ImageIcon className="w-4 h-4" />
              Also generate ad images <span className="text-zinc-500">(+5 credits each)</span>
            </label>
            <div className="text-xs text-zinc-500">
              Total cost: <span className="font-bold text-zinc-900 dark:text-zinc-100">{totalCredits} credits</span>
            </div>
          </div>

          <button
            type="button"
            onClick={generate}
            disabled={generating || selected.length === 0 || offer.trim().length < 10}
            data-testid="generate-ads-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-bold disabled:opacity-50"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating {selected.length} ads…</>
            ) : (
              <><Wand2 className="w-4 h-4" /> Generate ads for {selected.length} platform{selected.length === 1 ? "" : "s"}</>
            )}
          </button>
          {creditsRemaining !== null && (
            <div className="text-xs text-zinc-500 text-right" data-testid="credits-remaining">
              Credits remaining: <span className="font-bold">{creditsRemaining.toLocaleString()}</span>
            </div>
          )}
        </section>

        {/* Results */}
        {ads.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Your {ads.length} ads</h2>
              <button
                type="button"
                onClick={downloadAll}
                data-testid="download-all-btn"
                className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-900 dark:border-zinc-100 text-sm font-bold hover:bg-zinc-900 hover:text-white dark:hover:bg-zinc-100 dark:hover:text-zinc-900"
              >
                <Download className="w-4 h-4" /> Download all
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {ads.map((ad) => (
                <article
                  key={ad.platform}
                  data-testid={`ad-card-${ad.platform}`}
                  className="border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-4 flex flex-col gap-3"
                >
                  <header className="flex items-center justify-between">
                    <h3 className="font-bold flex items-center gap-2">
                      <span className="text-lg">{PLATFORM_ICONS[ad.platform] || "•"}</span>
                      {ad.platform_name}
                    </h3>
                    <span className={`text-xs ${ad.char_count > ad.char_limit ? "text-red-600 font-bold" : "text-zinc-500"}`}>
                      {ad.char_count}/{ad.char_limit}
                    </span>
                  </header>

                  {ad.error ? (
                    <div className="text-sm text-red-600">Failed: {ad.error}</div>
                  ) : (
                    <>
                      {ad.image_b64 && (
                        <img
                          src={`data:image/png;base64,${ad.image_b64}`}
                          alt={`${ad.platform_name} ad`}
                          className="w-full max-h-64 object-cover border border-zinc-200 dark:border-zinc-800"
                        />
                      )}
                      <div className="text-sm whitespace-pre-wrap leading-relaxed text-zinc-800 dark:text-zinc-200">
                        {ad.copy}
                      </div>
                      {ad.hashtags?.length > 0 && (
                        <div className="text-xs text-zinc-500 break-words">
                          {ad.hashtags.join("  ")}
                        </div>
                      )}
                      {(ad.hook || ad.cta) && (
                        <div className="text-xs text-zinc-500 border-t border-zinc-200 dark:border-zinc-800 pt-2 space-y-1">
                          {ad.hook && <div><span className="font-bold">Hook:</span> {ad.hook}</div>}
                          {ad.cta && <div><span className="font-bold">CTA:</span> {ad.cta}</div>}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 pt-2 mt-auto">
                        <button
                          type="button"
                          onClick={() => copyAd(ad)}
                          data-testid={`copy-ad-${ad.platform}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 dark:border-zinc-700 text-xs font-bold hover:bg-zinc-100 dark:hover:bg-zinc-900"
                        >
                          <CopyIcon className="w-3.5 h-3.5" /> Copy
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadOne(ad)}
                          data-testid={`download-ad-${ad.platform}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 dark:border-zinc-700 text-xs font-bold hover:bg-zinc-100 dark:hover:bg-zinc-900"
                        >
                          <Download className="w-3.5 h-3.5" /> .txt
                        </button>
                        {ad.image_b64 && (
                          <button
                            type="button"
                            onClick={() => downloadImage(ad)}
                            data-testid={`download-image-${ad.platform}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 dark:border-zinc-700 text-xs font-bold hover:bg-zinc-100 dark:hover:bg-zinc-900"
                          >
                            <ImageIcon className="w-3.5 h-3.5" /> Image
                          </button>
                        )}
                        {ad.share_url && (
                          <a
                            href={ad.share_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`share-ad-${ad.platform}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-xs font-bold hover:bg-zinc-700 dark:hover:bg-zinc-300"
                          >
                            <Share2 className="w-3.5 h-3.5" /> Share
                          </a>
                        )}
                      </div>
                    </>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
